(function () {
  'use strict';

  if (!window.__excludePublishers) return;

  var vendorSet = null;
  var handleSet = null;
  var isUpdating = false;
  var updateTimer = null;
  var backgroundCountHandle = null;

  var COUNT_CACHE_TTL = 10 * 60 * 1000;
  var LARGE_COLLECTION_THRESHOLD = 3000;
  var MEDIUM_COLLECTION_THRESHOLD = 800;
  var SAMPLE_PAGES = 2;
  var PARALLEL_BATCH = 5;

  function normalize(value) {
    return String(value || '').toLowerCase().trim();
  }

  function initSets() {
    if (vendorSet) return;
    vendorSet = new Set((window.__excludedPublisherVendors || []).map(normalize));
    handleSet = new Set((window.__excludedPublisherHandles || []).map(normalize));
  }

  function isExcludedVendor(vendor) {
    initSets();
    var normalized = normalize(vendor);
    if (!normalized) return false;
    return vendorSet.has(normalized) || handleSet.has(normalized);
  }

  function hasActiveFilters() {
    var params = new URLSearchParams(window.location.search);
    var keys = params.keys();
    var key = keys.next();
    while (!key.done) {
      if (key.value.indexOf('filter.') === 0 || key.value === 'sort_by' || key.value === 'q') {
        return true;
      }
      key = keys.next();
    }
    return false;
  }

  function getGridContext() {
    var grid = document.getElementById('product-grid');
    if (!grid || grid.dataset.exclusionPagination !== 'true') return null;
    return {
      grid: grid,
      handle: grid.dataset.collectionHandle,
      perPage: parseInt(grid.dataset.productsPerPage, 10) || 16,
      sectionId: grid.dataset.sectionId,
      allProductsCount: parseInt(grid.dataset.allProductsCount, 10) || 0,
      largeCollection: grid.dataset.largeCollection === 'true',
      container: document.getElementById('ProductGridContainer')
    };
  }

  function getCacheKey(handle) {
    var params = new URLSearchParams(window.location.search);
    return 'kitaboo-visible-count:' + handle + ':' + (params.get('sort_by') || 'default');
  }

  function readCachedCount(handle) {
    try {
      var raw = sessionStorage.getItem(getCacheKey(handle));
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (Date.now() - data.ts > COUNT_CACHE_TTL) return null;
      return data;
    } catch (error) {
      return null;
    }
  }

  function writeCachedCount(handle, count, exact) {
    try {
      sessionStorage.setItem(
        getCacheKey(handle),
        JSON.stringify({ count: count, exact: !!exact, ts: Date.now() })
      );
    } catch (error) {
      /* ignore quota errors */
    }
  }

  function setProductCount(total, options) {
    var opts = options || {};
    var text;
    if (total === 1) {
      text = '1 product';
    } else if (opts.approximate) {
      text = total + '+ products';
    } else {
      text = total + ' products';
    }

    document.querySelectorAll('#ProductCount, #ProductCountDesktop').forEach(function (el) {
      if (!el) return;
      el.textContent = text;
      el.dataset.countReady = 'true';
      if (opts.approximate) {
        el.dataset.countApproximate = 'true';
      } else {
        delete el.dataset.countApproximate;
      }
    });
  }

  function hidePendingCounts() {
    document.querySelectorAll('#ProductCount, #ProductCountDesktop').forEach(function (el) {
      if (el && el.dataset.countReady !== 'true') {
        el.textContent = '…';
      }
    });
  }

  function fetchProductsJsonPage(collectionHandle, page) {
    return fetch(
      '/collections/' + encodeURIComponent(collectionHandle) + '/products.json?limit=250&page=' + page
    )
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .catch(function () {
        return null;
      });
  }

  function countVisibleProducts(data) {
    if (!data || !data.products) return { visible: 0, raw: 0, hasMore: false };
    var visible = 0;
    data.products.forEach(function (product) {
      if (!isExcludedVendor(product.vendor)) visible += 1;
    });
    return {
      visible: visible,
      raw: data.products.length,
      hasMore: data.products.length >= 250
    };
  }

  function estimateVisibleCount(ctx) {
    var cached = readCachedCount(ctx.handle);
    if (cached) {
      setProductCount(cached.count, { approximate: !cached.exact });
      if (!cached.exact) scheduleBackgroundExactCount(ctx);
      return Promise.resolve(cached.count);
    }

    var pages = [];
    for (var i = 1; i <= SAMPLE_PAGES; i += 1) pages.push(i);

    return Promise.all(pages.map(function (page) { return fetchProductsJsonPage(ctx.handle, page); }))
      .then(function (results) {
        var totalRaw = 0;
        var totalVisible = 0;

        results.forEach(function (data) {
          var stats = countVisibleProducts(data);
          totalRaw += stats.raw;
          totalVisible += stats.visible;
        });

        if (totalRaw === 0) {
          setProductCount(0);
          writeCachedCount(ctx.handle, 0, true);
          return 0;
        }

        var ratio = totalVisible / totalRaw;
        var estimated = Math.max(totalVisible, Math.round(ctx.allProductsCount * ratio));
        setProductCount(estimated, { approximate: true });
        writeCachedCount(ctx.handle, estimated, false);
        scheduleBackgroundExactCount(ctx);
        return estimated;
      });
  }

  function fetchExactVisibleCount(collectionHandle) {
    var cached = readCachedCount(collectionHandle);
    if (cached && cached.exact) {
      setProductCount(cached.count);
      return Promise.resolve(cached.count);
    }

    var visible = 0;
    var page = 1;

    function fetchBatch(startPage) {
      var requests = [];
      for (var i = 0; i < PARALLEL_BATCH; i += 1) {
        requests.push(fetchProductsJsonPage(collectionHandle, startPage + i));
      }

      return Promise.all(requests).then(function (results) {
        var hasMore = false;
        results.forEach(function (data) {
          var stats = countVisibleProducts(data);
          visible += stats.visible;
          if (stats.hasMore) hasMore = true;
        });

        if (!hasMore) {
          writeCachedCount(collectionHandle, visible, true);
          setProductCount(visible);
          return visible;
        }

        return fetchBatch(startPage + PARALLEL_BATCH);
      });
    }

    return fetchBatch(1);
  }

  function scheduleBackgroundExactCount(ctx) {
    if (backgroundCountHandle === ctx.handle) return;
    backgroundCountHandle = ctx.handle;

    var run = function () {
      if (backgroundCountHandle !== ctx.handle) return;
      fetchExactVisibleCount(ctx.handle).finally(function () {
        backgroundCountHandle = null;
      });
    };

    if (window.requestIdleCallback) {
      window.requestIdleCallback(run, { timeout: 5000 });
    } else {
      window.setTimeout(run, 2500);
    }
  }

  function updateCollectionCount() {
    var ctx = getGridContext();
    if (!ctx || isUpdating) return Promise.resolve();

    if (hasActiveFilters()) {
      hidePendingCounts();
      document.querySelectorAll('#ProductCount, #ProductCountDesktop').forEach(function (el) {
        if (el) el.dataset.countReady = 'true';
      });
      return Promise.resolve();
    }

    isUpdating = true;

    var countPromise;
    if (ctx.largeCollection || ctx.allProductsCount > LARGE_COLLECTION_THRESHOLD) {
      countPromise = estimateVisibleCount(ctx);
    } else if (ctx.allProductsCount > MEDIUM_COLLECTION_THRESHOLD) {
      var cached = readCachedCount(ctx.handle);
      if (cached) {
        setProductCount(cached.count, { approximate: !cached.exact });
        countPromise = Promise.resolve(cached.count);
        if (!cached.exact) scheduleBackgroundExactCount(ctx);
      } else {
        countPromise = fetchExactVisibleCount(ctx.handle);
      }
    } else {
      countPromise = fetchExactVisibleCount(ctx.handle);
    }

    return countPromise.finally(function () {
      isUpdating = false;
    });
  }

  function scheduleUpdate() {
    if (updateTimer) window.clearTimeout(updateTimer);
    updateTimer = window.setTimeout(updateCollectionCount, 50);
  }

  document.addEventListener('DOMContentLoaded', function () {
    var grid = document.getElementById('product-grid');
    if (!grid || grid.dataset.exclusionPagination !== 'true') {
      document.querySelectorAll('#ProductCount, #ProductCountDesktop').forEach(function (el) {
        if (el) el.dataset.countReady = 'true';
      });
      return;
    }

    hidePendingCounts();
    scheduleUpdate();
  });

  document.addEventListener('product-grid:updated', function () {
    if (isUpdating || hasActiveFilters()) return;
    scheduleUpdate();
  });

  window.addEventListener('popstate', scheduleUpdate);
})();
